import { useContext } from 'react';

import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import { logger } from 'utils/logger';
import { ERROR_REASONS } from 'utils/logger/transports/constants';

import { AuthContext } from 'pages/authentication/AuthProvider';

import { AuthEvent } from 'constants/authEvents';

export interface TokenPayload {
    email: string;
    'cognito:groups': string[];
}

type EventErrorReasons = Record<string, string>;

const eventErrorReasons: EventErrorReasons = {
    [AuthEvent.TOKEN_REFRESH]: ERROR_REASONS.tokenRefresh,
    [AuthEvent.TOKEN_REFRESH_FAILURE]: ERROR_REASONS.tokenRefreshFailed,
};

/**
 * useAuth hooks
 *
 * @returns {Object} - Object with login, logout, getPermission, getAccessToken, getUserEmail functions
 */
const useAuth = () => {
    const context = useContext(AuthContext);
    const login = () => {
        try {
            void context?.handleSignIn();
        } catch (err: unknown) {
            logger.error(Error('ACL: login error'), {
                cause: err,
                tags: {
                    reason: ERROR_REASONS.login,
                    errorMsg: err instanceof Error ? err.message : '', // Amplify AuthError error doesn't include status property. It has only 'message' and 'log'
                },
            });
        }
    };

    const getPermission = async () => {
        try {
            const session = await fetchAuthSession();
            const payload = session.tokens?.idToken?.payload as unknown as TokenPayload;

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            const groups = payload['cognito:groups'] || [];
            return groups;
        } catch (err: unknown) {
            logger.error(Error('ACL: getPermission error'), {
                cause: err,
                tags: {
                    reason: ERROR_REASONS.permission,
                    errorMsg: err instanceof Error ? err.message : '', // Amplify AuthError error doesn't include status property. It has only 'message' and 'log'
                },
            });
            return []; // TODO: Fix getPermission error handling
        }
    };

    const getAccessToken = async (forceRefresh = false) => {
        try {
            const session = await fetchAuthSession({ forceRefresh });
            const token = session.tokens?.idToken?.toString();

            // If no token and not already forcing refresh, try with force refresh
            if (!token && !forceRefresh) {
                try {
                    const refreshedSession = await fetchAuthSession({ forceRefresh: true });
                    return refreshedSession.tokens?.idToken?.toString() ?? null;
                } catch (refreshErr: unknown) {
                    // Force refresh failed, tokens are likely expired
                    logger.error(Error('ACL: getAccessToken force refresh failed'), {
                        cause: refreshErr,
                        tags: {
                            reason: ERROR_REASONS.getAccessToken,
                            errorMsg: refreshErr instanceof Error ? refreshErr.message : String(refreshErr),
                        },
                    });
                    return null;
                }
            }

            return token ?? null;
        } catch (err: unknown) {
            logger.error(Error('ACL: getAccessToken error'), {
                cause: err,
                tags: {
                    reason: ERROR_REASONS.getAccessToken,
                    errorMsg: err instanceof Error ? err.message : '', // Amplify AuthError error doesn't include status property. It has only 'message' and 'log'
                },
            });
            return null;
        }
    };

    const getUserEmail = async () => {
        try {
            const session = await fetchAuthSession();

            return session.tokens?.idToken?.payload.email;
        } catch (err: unknown) {
            logger.error(Error('ACL: getUserEmail error'), {
                cause: err,
                tags: {
                    reason: ERROR_REASONS.userEmail,
                    errorMsg: err instanceof Error ? err.message : '', // Amplify AuthError error doesn't include status property. It has only 'message' and 'log'
                },
            });
            return null; // TODO: Fix getUserEmail error handling
        }
    };

    const logout = () => {
        void context?.handleSignOut();
        localStorage.clear();
    };

    const handleSessionError = (err: unknown, reason: string) => {
        logger.error(Error(`ACL: ${reason}`), {
            cause: err,
            tags: {
                reason,
                errorMsg: err instanceof Error ? err.message : '', // Amplify AuthError error doesn't include status property. It has only 'message' and 'log'
            },
        });
        logout();
    };

    Hub.listen('auth', (data) => {
        const { payload } = data;
        const reason = eventErrorReasons[payload.event];
        if (reason) {
            fetchAuthSession().catch((err: unknown) => {
                handleSessionError(err, reason);
            });
            // TODO: Refine error handling there, cause it will just throw into upper context
        }
    });

    return {
        login,
        logout,
        getPermission,
        getAccessToken,
        getUserEmail,
        loading: context?.loading,
    };
};

export default useAuth;
